import React from "react";
import { Dialog, DialogContent, DialogHeader } from "@shared/ui/dialog";
import { X, Edit, Clock, Loader2 } from "lucide-react";
import { Button } from "@shared/ui/button";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { Tabs } from "@shared/ui/tabs";
import { TimelineTab } from "@features/cx/shared/components/tabs/TimelineTab";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useGlCodeDetails } from "../../hooks";
import { useNavigate } from "react-router-dom";

interface DetailsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  data?: any; // To pass the GL code row data later
}

const Badge = ({
  children,
  variant = "blue",
}: {
  children: React.ReactNode;
  variant?: "blue" | "green" | "gray";
}) => {
  const styles = {
    blue: "bg-[#f0f7ff] text-[#1a73e8]",
    green: "bg-[#e6f4ea] text-[#1e8e3e]",
    gray: "bg-gray-100 text-gray-700",
  };
  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${styles[variant]}`}
    >
      {children}
    </span>
  );
};

export const DetailsModal: React.FC<DetailsModalProps> = ({
  open,
  onOpenChange,
  data,
}) => {
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();
  const { data: details, isLoading } = useGlCodeDetails(data?.glCode);

  const isActive =
    details?.status === "1" ||
    details?.status === 1 ||
    details?.status === true;

  const glIdentityDetails = details
    ? [
      {
        label: "GL Type",
        value: <Badge variant="blue">{details.glTypeDisplay}</Badge>,
      },
      {
        label: "GL code",
        value: (
          <span className="font-bold text-[#1a73e8]">{details.glCode}</span>
        ),
      },
      {
        label: "GL Name",
        value: (
          <span className="font-medium text-gray-900">{details.glName}</span>
        ),
      },
      {
        label: "Status",
        value: (
          <Badge variant={isActive ? "green" : "gray"}>
            {isActive ? (
              <span className="w-1.5 h-1.5 rounded-full bg-[#1e8e3e] mr-1.5"></span>
            ) : null}
            {details.statusDisplay || (isActive ? "Active" : "Inactive")}
          </Badge>
        ),
      },
    ]
    : [];

  const mappingRules = details
    ? [
      {
        label: "Source",
        badges: (details.sources || []).map((s: any) => ({
          text: s.label || s,
          variant: "blue",
        })),
      },
      {
        label: "Transaction Category",
        badges: (details.transactionCategories || []).map((c: any) => ({
          text: c.label || c,
          variant: "green",
        })),
      },
      {
        label: "Transaction Type",
        badges: (details.transactionTypes || []).map((t: any) => ({
          text: t.label || t,
          variant: "gray",
        })),
      },
    ]
    : [];

  const systemInfo = details
    ? [
      {
        label: "Created By",
        user: details.createdUserRemoteName || "System Admin",
        date: details.createdAt || "N/A",

      },
      {
        label: "Updated By",
        user: details.updatedUserRemoteName || "System Admin",
        date: details.updatedAt || "N/A",
      },
    ]
    : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl gap-0 p-0 overflow-hidden bg-white [&>button]:hidden">
        {/* Header Section */}
        <div className="px-6 pt-6 pb-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center px-4 bg-[#f0f7ff] text-[#1a73e8] rounded-xl font-bold text-base h-[52px] min-w-[52px]">
                {details?.glCode || data?.glCode}
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <h2 className="text-md font-bold text-gray-900">
                    {details?.glName || data?.glName}
                  </h2>
                  <Badge variant="blue">
                    {details?.glTypeDisplay || data?.glType}
                  </Badge>
                  <Badge variant={isActive ? "green" : "gray"}>
                    {isActive ? (
                      <span className="w-1.5 h-1.5 rounded-full bg-[#1e8e3e] mr-1.5"></span>
                    ) : null}
                    {details?.statusDisplay ||
                      (isActive ? "Active" : "Inactive")}
                  </Badge>
                </div>
                {/* <div className="text-sm text-gray-500 font-medium flex items-center gap-2">
                  <span>{details?.glCode || data?.glCode}</span>
                  <span>·</span>
                  <span>Header</span>
                  <span>·</span>
                  <span>Level 1</span>
                </div> */}
              </div>
            </div>
            <button
              onClick={() => onOpenChange(false)}
              className="p-1.5 border-[1.5px] border-black text-black hover:bg-gray-50 rounded-xl transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <Tabs
            defaultValue="details"
            className="w-full mt-6"
            tabsListClassName="flex border-b border-gray-100 bg-transparent rounded-none px-6 gap-6 h-auto p-0"
            urlMapping={{
              details: "details",
              auditTrail: "auditTrail",
            }}
            tabComponents={{
              details: {
                label: "Details",
                component: (
                  <div className="overflow-y-auto max-h-[60vh] space-y-4">
                    {isLoading ? (
                      <div className="flex justify-center items-center py-10">
                        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                      </div>
                    ) : (
                      <>
                        {/* GL Identity Card */}
                        <div className="border border-gray-200 rounded-xl overflow-hidden">
                          <div className="bg-gray-50 border-b border-gray-200 px-5 py-3">
                            <h3 className="text-sm font-bold text-gray-800">
                              GL Identity
                            </h3>
                          </div>
                          <div className="p-5">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-y-6 gap-x-8">
                              {glIdentityDetails.map((detail, idx) => (
                                <ValueLabel
                                  key={idx}
                                  label={detail.label}
                                  value={detail.value}
                                />
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Mapping Rules */}
                        {mappingRules.length > 0 &&
                          mappingRules.map((rule, idx) => (
                            <div
                              key={idx}
                              className="border border-gray-200 rounded-xl p-5"
                            >
                              <ValueLabel
                                label={rule.label}
                                value={
                                  <div className="flex flex-wrap gap-2 mt-2">
                                    {rule.badges.map((badge, bIdx) => (
                                      <Badge key={bIdx} variant={badge.variant}>
                                        {badge.text}
                                      </Badge>
                                    ))}
                                  </div>
                                }
                              />
                            </div>
                          ))}

                        {/* System Info Card */}
                        <div className="border border-gray-200 rounded-xl p-5">
                          <h3 className="text-sm font-bold text-gray-900 mb-5">
                            System Information
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-8">
                            {systemInfo.map((info, idx) => (
                              <ValueLabel
                                key={idx}
                                label={info.label}
                                value={
                                  <div className="flex flex-col gap-0.5 mt-1">
                                    <span className="text-sm text-gray-900 font-medium">
                                      {info.user}
                                    </span>
                                    <span className="text-xs text-gray-400 font-normal">
                                      {info.date}
                                    </span>
                                  </div>
                                }
                              />
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ),
              },
              auditTrail: {
                label: "Audit Trail",
                icon: <Clock className="w-4 h-4" />,
                component: (
                  <div>
                    <TimelineTab
                      remoteUtilityId={Number(remoteUtilityId)}
                      entityId={data?.id?.toString() || ""}
                      actionType="gl_code"
                    />
                  </div>
                ),
              },
            }}
            disableUrlSync={true}
            level={0}
          />
        </div>

        {/* Footer Section */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between bg-white rounded-b-xl">
          <Button
            variant="outline"
            className="text-gray-700 font-medium"
            onClick={() => navigate(`/bx/gl-code/edit/${data?.glCode}`)}
          >
            <Edit className="w-4 h-4 mr-2" />
            Edit
          </Button>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="text-gray-700 font-medium"
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
